import {Injectable} from '@angular/core';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import {Store} from '@ngxs/store';

import {GlobalStateModel} from '@app/store/state.model';
import {Logger} from '../logger.service';
import {CredentialsService} from './credentials.service';

const log = new Logger('AuthenticationGuard');

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  constructor(
    private router: Router,
    private credentialsService: CredentialsService,
    private store: Store
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    if (this.credentialsService.isAuthenticated() || this.hasStoreSession()) {
      return true;
    }

    log.debug('Not authenticated, redirecting and adding redirect url...');
    this.router.navigate(['/sign-in'], {
      queryParams: {redirect: state.url},
      replaceUrl: true,
    });
    return false;
  }

  /**
   * `CredentialsService` is a second copy of the session, kept in Capacitor
   * Preferences (or sessionStorage), and it drifts from the NGXS `user` state
   * that everything else reads. Known ways in: pre-migration sign-ins that
   * left "remember me" unchecked wrote credentials to sessionStorage only, so
   * they vanish on browser restart while the storage plugin rehydrates
   * `user.info` from localStorage (see the shell's recoverOrLogout, added in
   * 5f103c11); `guest-info` replaces them with a guest record and deletes the
   * persisted copy. In that split state the navbar still shows
   * ACCOUNT/LOGOUT and the menu banner still shows the Favorite/Recent Orders
   * buttons, but this guard denied the user — bouncing them through /sign-in
   * and out to the locations page.
   *
   * `state.user.info.authtoken` is what the rest of the app authenticates
   * with (navbar, header banner, GuestGuard, the refresh interceptor), so the
   * guard follows it too. A token that has since expired is not this guard's
   * problem to detect: the first API call 401s and OloAuthRefreshInterceptor
   * refreshes it or signs the user out.
   */
  private hasStoreSession(): boolean {
    return !!this.store.selectSnapshot(
      (state: GlobalStateModel) => state.user?.info?.authtoken
    );
  }
}
